import { BOND_PRINCIPAL_KIND } from "./bondPrincipalConfig";
import { HISTORIC_GUARDIAN_FIELD_KEYS } from "../../../pages/SHARED/Bond Creation Historic/historicGuardianConfig";

/** @typedef {'fields-location' | 'zip-code'} BondPrincipalZipLookup */

/**
 * Per bond-type principal step UI — field storage keys, labels, and feature flags.
 * Keys match each Principals / Trustees / PersonalRepresentatives / Guardians component.
 */
export const BOND_PRINCIPAL_FIELD_CONFIG = Object.freeze({
  [BOND_PRINCIPAL_KIND.va]: {
    kind: BOND_PRINCIPAL_KIND.va,
    fieldKeys: HISTORIC_GUARDIAN_FIELD_KEYS.va,
    nameSuggestionDebounceMs: 600,
    logPgNameSuggestion: true,
    zipLookup: "fields-location",
    guardianCountKey: "PrincipalNumber",
    pgLookupBoxSx: { mt: 4 },
    attorneyUi: "va",
    emailUi: "newPgOrGlobalEsign",
    bondAmountSsnThreshold: 250000,
    bondAmountSsnOp: "gte",
    showManualOccupation: true,
    manualOccupationWhen: "highBondNotPgNotAttorney",
    relationshipLabel: "Relationship to Beneficiary",
    relationshipRequired: true,
    relationshipShowWhen: "notPg",
    googlePlaceholderManual: "Search Address",
    manualStyles: { name: { mt: 1, mb: 1 } },
    pgChangeClearsAttorney: false,
    clearFieldsOnPgNo: false,
    clearFieldsOnAttorneyNo: false,
    pgOnChangeUsesUpdateGuardianAt: true,
    toastOnIncompletePgAddress: true,
    pgFetchIncludesBilling: true,
  },
  [BOND_PRINCIPAL_KIND.trustee]: {
    kind: BOND_PRINCIPAL_KIND.trustee,
    fieldKeys: HISTORIC_GUARDIAN_FIELD_KEYS.trustee,
    nameSuggestionDebounceMs: 1000,
    logPgNameSuggestion: false,
    zipLookup: "fields-location",
    guardianCountKey: "PrincipalNumber",
    pgLookupBoxSx: { mt: 4 },
    attorneyUi: "trustee",
    emailUi: "newPgOrGlobalEsign",
    bondAmountSsnThreshold: 250000,
    bondAmountSsnOp: "gte",
    showManualOccupation: true,
    manualOccupationWhen: "highBondNotPgNotAttorney",
    relationshipLabel: "Relationship to Beneficiary",
    relationshipRequired: true,
    relationshipShowWhen: "notPg",
    googlePlaceholderManual: "Search Address",
    manualStyles: { name: { mt: 1, mb: 1 } },
    pgChangeClearsAttorney: true,
    clearFieldsOnPgNo: false,
    clearFieldsOnAttorneyNo: false,
    pgOnChangeUsesUpdateGuardianAt: true,
    toastOnIncompletePgAddress: true,
    pgFetchIncludesBilling: true,
  },
  [BOND_PRINCIPAL_KIND.probate]: {
    kind: BOND_PRINCIPAL_KIND.probate,
    fieldKeys: HISTORIC_GUARDIAN_FIELD_KEYS.probate,
    nameSuggestionDebounceMs: 1000,
    logPgNameSuggestion: false,
    zipLookup: "fields-location",
    guardianCountKey: "PrincipalNumber",
    pgLookupBoxSx: null,
    attorneyUi: "probate",
    emailUi: "newPgOrGlobalEsignWithNote",
    bondAmountSsnThreshold: 250000,
    bondAmountSsnOp: "gte",
    showManualOccupation: true,
    manualOccupationWhen: "highBondNotPgNotAttorney",
    relationshipLabel: "What is the principal's relationship to the deceased?",
    relationshipRequired: false,
    relationshipShowWhen: "notPgNotAttorney",
    googlePlaceholderManual: "Search Principal Address",
    manualStyles: { name: { mb: 2 } },
    pgChangeClearsAttorney: true,
    clearFieldsOnPgNo: true,
    clearFieldsOnAttorneyNo: true,
    pgOnChangeUsesUpdateGuardianAt: true,
    toastOnIncompletePgAddress: true,
    pgFetchIncludesBilling: true,
  },
  [BOND_PRINCIPAL_KIND.guardianship]: {
    kind: BOND_PRINCIPAL_KIND.guardianship,
    fieldKeys: {
      ...HISTORIC_GUARDIAN_FIELD_KEYS.guardianship,
      ssn: "ssn",
    },
    nameSuggestionDebounceMs: 1000,
    logPgNameSuggestion: false,
    zipLookup: "zip-code",
    guardianCountKey: "GuardianNumber",
    pgLookupBoxSx: null,
    attorneyUi: "guardianship",
    emailUi: "newPgOrPrincipalEsignWithNote",
    bondAmountSsnThreshold: 250000,
    bondAmountSsnOp: "gte",
    showManualOccupation: true,
    manualOccupationWhen: "highBondNotPgNotAttorney",
    relationshipLabel: "Relationship to Ward",
    relationshipRequired: true,
    relationshipShowWhen: "notPg",
    googlePlaceholderManual: "Search Address",
    manualStyles: { name: { mt: 2, mb: 2 } },
    pgChangeClearsAttorney: true,
    clearFieldsOnPgNo: false,
    clearFieldsOnAttorneyNo: true,
    pgOnChangeUsesUpdateGuardianAt: false,
    toastOnIncompletePgAddress: true,
    pgFetchIncludesBilling: true,
    pgFetchIncludesGiaLoa: true,
  },
});

export function getBondPrincipalFieldConfig(kind) {
  const cfg = BOND_PRINCIPAL_FIELD_CONFIG[kind];
  if (!cfg) {
    throw new Error(`getBondPrincipalFieldConfig: unknown kind "${kind}"`);
  }
  return cfg;
}

export function gPrincipalVal(guardian, keys, logical) {
  const k = keys[logical];
  return guardian?.[k] ?? "";
}
